import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ALL_DRESSCODES } from '../../constants/EventConstants';
import { formatDay } from './pictureParts';

const SHOWING = {
  NONE: 'Kein Bild',
  HIDDEN: 'Bild nicht freigegeben',
  SYMBOL: 'KI-Symbolbild',
};

const euro = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const amount = Number(value);
  if (!Number.isFinite(amount)) return null;
  if (amount === 0) return 'Gratis';
  return new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR' }).format(amount);
};

const clock = (value) => (value || '').slice(0, 5);

/** "Di., 04.11. · 19:00–22:00" */
export const whenOf = (event) => {
  const start = clock(event.startTime);
  const end = clock(event.endTime);
  const time = start && end ? `${start}–${end}` : start;
  return `${formatDay(event.eventDate)}${time ? ` · ${time}` : ''}`;
};

function Facts({ event }) {
  const dressCode = ALL_DRESSCODES.find((each) => each.value === event.dressCode)?.label ?? event.dressCode;
  const rows = [
    ['Ort', event.address],
    ['Veranstalter', event.account && (
      <a href={`https://www.instagram.com/${event.account}/`} target="_blank" rel="noreferrer">@{event.account}</a>
    )],
    ['Genres', (event.musicGenre ?? []).join(', ')],
    ['Acts', (event.artists ?? []).join(', ')],
    ['Eintritt', euro(event.entryPrice)],
    ['Abendkassa', euro(event.boxOfficePrice)],
    ['Mindestalter', event.minAge ? `${event.minAge} Jahre` : null],
    ['Dresscode', dressCode],
    ['Awareness-Team', event.hasAwarenessTeam ? 'Ja' : null],
    ['Tickets', event.ticketUrl && <a href={event.ticketUrl} target="_blank" rel="noreferrer">Ticket-Link öffnen</a>],
  ].filter(([, value]) => value);
  return (
    <dl className="isy-facts-list">
      {rows.map(([label, value]) => (
        <React.Fragment key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

/**
 * One symbol picture large next to everything about the event, to choose well: browse the
 * pictures of its kind (arrows or ← →), set the one shown for this date with "Festlegen", or
 * for every date of a recurring event at once. The dialog is the confirmation, also when a
 * hidden picture gets replaced.
 */
export default function SymbolPreviewDialog({ event, series = [], pictures, startIndex, onAssign, onAssignAll, onClose }) {
  const [index, setIndex] = useState(startIndex);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const assignRef = useRef(null);

  const picture = pictures[index];
  const chosen = picture.url === event.image;
  // Every date of the recurring event, this one included, by date.
  const dates = [event, ...series].sort((a, b) => String(a.eventDate).localeCompare(String(b.eventDate)));
  const allChosen = dates.every((each) => each.image === picture.url);
  const hiddenElsewhere = series.filter((each) => each.showing === 'HIDDEN').length;

  const step = useCallback((by) => {
    setFailed(false);
    setIndex((current) => (current + by + pictures.length) % pictures.length);
  }, [pictures.length]);

  useEffect(() => { assignRef.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (key) => {
      if (key.key === 'Escape') onClose();
      if (key.key === 'ArrowLeft') step(-1);
      if (key.key === 'ArrowRight') step(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, step]);

  const run = async (action) => {
    setSaving(true);
    setFailed(false);
    const done = await action();
    setSaving(false);
    if (done) onClose();
    else setFailed(true);
  };

  const description = (event.descriptionGerman || '').trim() || (event.descriptionEnglish || '').trim();
  const englishOnly = !(event.descriptionGerman || '').trim() && description;

  return (
    <>
      <div className="apd-scrim" onClick={onClose} aria-hidden="true" />
      <div className="apd isy-preview" role="dialog" aria-modal="true" aria-label={`KI-Symbolbild für ${event.title}`}>
        <header className="apd-head">
          <div>
            <h2 className="apd-title">{event.title}</h2>
            <p className="apd-sub">{whenOf(event)}</p>
          </div>
          <button type="button" className="irp-close" onClick={onClose} aria-label="Schließen">✕</button>
        </header>

        <div className="isy-preview-body">
          <figure className="isy-preview-figure">
            <div className="isy-preview-stage">
              {pictures.length > 1 && (
                <button type="button" className="isy-preview-nav isy-preview-nav--prev" onClick={() => step(-1)}
                  aria-label="Voriges Bild">‹</button>
              )}
              <img className="isy-preview-image" src={picture.url} alt={picture.name} />
              {pictures.length > 1 && (
                <button type="button" className="isy-preview-nav isy-preview-nav--next" onClick={() => step(1)}
                  aria-label="Nächstes Bild">›</button>
              )}
            </div>
            <figcaption className="isy-preview-caption">
              <strong>{picture.name}</strong> · {index + 1} von {pictures.length}
            </figcaption>
          </figure>

          <section className="isy-preview-info" aria-label="Infos zum Event">
            <div className="isy-head">
              <span className={'ir-visibility' + (event.showing === 'SYMBOL' ? ' is-on' : '')}>{SHOWING[event.showing]}</span>
              {!event.live && <span className="ir-badge">Event wartet auf Freigabe</span>}
            </div>
            <Facts event={event} />
            <h3 className="isy-preview-heading">Beschreibung{englishOnly ? ' (nur Englisch)' : ''}</h3>
            {description
              ? <p className="isy-preview-description">{description}</p>
              : <p className="isy-preview-empty">Keine Beschreibung.</p>}
            {series.length > 0 && (
              <>
                <h3 className="isy-preview-heading">Termine dieser Reihe ({dates.length})</h3>
                <ul className="isy-preview-dates">
                  {dates.map((each) => (
                    <li key={each.id} className={each.id === event.id ? 'is-current' : ''}>
                      <span>{whenOf(each)}</span>
                      <span className="isy-preview-date-state">
                        {each.image === picture.url ? 'dieses Bild' : SHOWING[each.showing]}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>
        </div>

        {event.showing === 'HIDDEN' && !chosen && (
          <p className="apd-note apd-note--warn isy-preview-warn">
            Das nicht freigegebene Bild dieses Termins wird ersetzt und ist danach weg.
          </p>
        )}
        {series.length > 0 && hiddenElsewhere > 0 && !allChosen && (
          <p className="apd-note apd-note--warn isy-preview-warn">
            Für alle Termine: Bei {hiddenElsewhere} {hiddenElsewhere === 1 ? 'weiterem Termin wird ein nicht freigegebenes Bild' : 'weiteren Terminen werden nicht freigegebene Bilder'} ersetzt.
          </p>
        )}
        {failed && <p className="apd-note apd-note--warn isy-preview-warn">Konnte das Bild nicht festlegen. Bitte nochmal versuchen.</p>}

        <footer className="apd-actions">
          <button ref={assignRef} type="button" className="apd-btn apd-btn--primary"
            onClick={() => run(() => onAssign(event, picture))} disabled={chosen || saving}>
            {chosen ? 'Ist schon festgelegt' : saving ? 'Speichert …' : 'Festlegen'}
          </button>
          {series.length > 0 && (
            <button type="button" className="apd-btn apd-btn--primary"
              onClick={() => run(() => onAssignAll(dates, picture))} disabled={allChosen || saving}>
              {allChosen ? 'Alle Termine haben es schon' : `Für alle ${dates.length} Termine festlegen`}
            </button>
          )}
          <button type="button" className="apd-btn" onClick={onClose} disabled={saving}>Abbrechen</button>
        </footer>
      </div>
    </>
  );
}
